import { Link, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  ConsultationBand,
  PublicHero,
  PublicShell,
  type PublicHeroImage,
} from "@/components/PublicShell"
import { BrochureButton, useBrochureAvailable } from "@/components/public/BrochureGate"
import { PublicMeta } from "@/components/public/PublicMeta"
import type { GalleryItemProps, OccasionServerProps, PageProps } from "@/types/inertia"
import "@/styles/public-occasions.css"
import "@/styles/public-gallery.css"

// Shared layout for the five occasion pages, built to the Private Cellar
// mockup's section order: hero -> use-case story (with the brochure) ->
// "from the cellar" strip of real bottles -> How It Works teaser ->
// ConsultationBand. From M7 the cellar strip shows the gallery pieces
// Duncan has tagged with the occasion (the `gallery_items` prop), and falls
// back to the page's own bottles while there are none.

export type CellarBottle = {
  src: string
  alt: string
  name: string
  place: string
}

export interface OccasionPageProps {
  title: string
  /** The search title (keyword-led), when it differs from the page's h1. */
  metaTitle?: string
  description: string
  lede: string
  heroImage: PublicHeroImage
  story: {
    eyebrow?: string
    heading: string
    paragraphs: string[]
    quote?: string
  }
  cellar: {
    heading?: string
    /** Introduces the fallback bottles; the gallery pieces have their own line. */
    lede?: string
    /** Shown only while the occasion has no gallery pieces. */
    bottles: CellarBottle[]
  }
}

const STEPS = [
  { numeral: "I", title: "Choose your wine", body: "We guide you through the range and send samples." },
  { numeral: "II", title: "Share your artwork", body: "A logo, a crest, a photograph — or nothing at all." },
  { numeral: "III", title: "We design the label", body: "Drawn up and sent to you as a proof to review." },
  { numeral: "IV", title: "Approve the proof", body: "Nothing is printed until you are entirely happy." },
  { numeral: "V", title: "Delivered to you", body: "To your venue, office or door, anywhere in the UK." },
]

export function OccasionPage({
  title,
  metaTitle,
  description,
  lede,
  heroImage,
  story,
  cellar,
}: OccasionPageProps) {
  const { props } = usePage<PageProps<Partial<OccasionServerProps>>>()
  const pieces = props.gallery_items ?? []
  const galleryHref = props.occasion ? `/gallery?occasion=${props.occasion.slug}` : "/gallery"

  return (
    <PublicShell>
      <PublicMeta title={metaTitle ?? title} description={description} />

      <PublicHero
        eyebrow="Occasions"
        title={title}
        lede={lede}
        image={heroImage}
        actions={
          <>
            <Button asChild variant="gold" size="lg">
              <Link href="/contact">Request a Consultation</Link>
            </Button>
            <Button asChild variant="ghost-hairline" size="lg">
              <Link href="/wines">View Our Wines</Link>
            </Button>
          </>
        }
      />

      <section className="band band-black">
        <div className="shell">
          <div className="occ-story">
            {story.eyebrow && <div className="eyebrow">{story.eyebrow}</div>}
            <h2>{story.heading}</h2>
            {story.paragraphs.map((paragraph, index) => (
              <p className="lede" key={index}>
                {paragraph}
              </p>
            ))}
            {story.quote && <blockquote>{story.quote}</blockquote>}
            <StoryBrochure />
          </div>
        </div>
      </section>

      <section className="band band-charcoal">
        <div className="shell">
          <div className="occ-band-head occ-band-head-center">
            <div className="eyebrow eyebrow-center">From the cellar</div>
            <h2>{cellar.heading ?? "Labels we have made"}</h2>
            {pieces.length > 0 ? (
              <p className="lede">A few of the bottles we have made for the occasion, from our gallery.</p>
            ) : (
              cellar.lede && <p className="lede">{cellar.lede}</p>
            )}
          </div>
          <div className="occ-cellar-grid">
            {pieces.length > 0
              ? pieces.map((piece) => <CellarPiece key={piece.id} piece={piece} />)
              : cellar.bottles.map((bottle) => (
                  <div className="occ-cellar-card" key={bottle.src}>
                    <div className="occ-cellar-frame">
                      <img src={bottle.src} alt={bottle.alt} />
                    </div>
                    <div className="occ-cellar-meta">
                      <strong>{bottle.name}</strong>
                      <span>{bottle.place}</span>
                    </div>
                  </div>
                ))}
          </div>
          {pieces.length > 0 && (
            <div className="occ-steps-cta occ-cellar-more">
              <Button asChild variant="ghost-hairline" size="lg">
                <Link href={galleryHref}>See them in the gallery</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="band band-black">
        <div className="shell">
          <div className="occ-band-head">
            <div className="eyebrow">How it works</div>
            <h2>Five unhurried steps</h2>
          </div>
          <div className="occ-steps">
            {STEPS.map((step) => (
              <div className="occ-step" key={step.numeral}>
                <em>{step.numeral}</em>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
          <div className="occ-steps-cta">
            <Button asChild variant="ghost-hairline" size="lg">
              <Link href="/how-it-works">See How It Works in full</Link>
            </Button>
          </div>
        </div>
      </section>

      <ConsultationBand />
    </PublicShell>
  )
}

/** A gallery piece in the cellar strip: the same frame and rule as the fallback bottles. */
function CellarPiece({ piece }: { piece: GalleryItemProps }) {
  return (
    <div className="occ-cellar-card">
      <div className="gal-frame">
        <img
          src={piece.thumb_url}
          alt={piece.title}
          width={piece.width ? Math.round(piece.width / 2) : undefined}
          height={piece.height ? Math.round(piece.height / 2) : undefined}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="occ-cellar-meta">
        <strong>{piece.title}</strong>
        {piece.caption && <p className="gal-caption">{piece.caption}</p>}
      </div>
    </div>
  )
}

/** The brochure below the story (M7 decision 12); nothing without a brochure. */
function StoryBrochure() {
  if (!useBrochureAvailable()) return null

  return (
    <div className="brochure-cta">
      <BrochureButton />
      <p>The wines, the five steps and bottles we have made, in one PDF.</p>
    </div>
  )
}
